"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, ArrowRight } from "lucide-react";
import { getAppUrl, getRootUrl, useTheme } from "@lyder-talk/core";
import { useLocation } from "react-router-dom";
import { ThemeToggle } from "@lyder-talk/ui";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { APP_NAME } from "@/lib/marketing-constants";

const NAV_LINKS = [
  { name: "Features", href: "/features" },
  { name: "About", href: "/about" },
  { name: "Blog", href: "/blog" },
  { name: "FAQ", href: "/faq" },
  { name: "Updates", href: "/updates" },
] as const;

const LandingHeader = () => {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { pathname } = useLocation();
  const [isStuck, setIsStuck] = useState(false);

  /**
   * The landing page opens on a dark, aurora-lit hero, so the header has
   * to belong to that surface until the page scrolls — a white bar over a
   * night sky reads as a mistake. Everywhere else, and everywhere on the
   * landing page once it is scrolled, it is the normal paper header.
   */
  const onDark = pathname === "/" && !isStuck;
  const themeToggleClass = onDark
    ? "text-white/60 hover:bg-white/10 hover:text-white"
    : "";

  useEffect(() => {
    /*
     * A scroll listener, not a sentinel. The obvious sentinel — a 1px div
     * at the top of the header — is pinned to the viewport along with the
     * sticky header, so it never leaves the viewport and `isStuck` never
     * flips. Moving that sentinel into a wrapper above the header fixes
     * the observation but breaks the sticky: the wrapper becomes the
     * containing block and is only as tall as the header, leaving the
     * header nowhere to stick. Eight pixels of scroll is the whole
     * question anyway.
     */
    const onScroll = () => setIsStuck(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSignIn = () => {
    window.open(getAppUrl("/auth/signin"), "_blank", "noopener,noreferrer");
  };

  const handleNav = (href: string) => {
    window.location.href = getRootUrl(href);
  };

  return (
    <header className="sticky top-0 z-30 w-full">
      <div
        className={cn(
          "border-b transition-shadow duration-200",
          isStuck
            ? "border-border/60 bg-background/85 shadow-[0_4px_16px_-8px_rgba(15,23,42,0.12)] backdrop-blur supports-[backdrop-filter]:bg-background/70"
            : onDark
              ? "border-transparent bg-transparent"
              : "border-transparent bg-background",
        )}
      >
        <div className="container mx-auto flex h-16 max-w-6xl items-center gap-4 px-4">
          {/* Brand */}
          <button
            type="button"
            onClick={() => (window.location.href = "/")}
            className="group flex shrink-0 items-center gap-2 rounded-md focus-ring"
            aria-label={`${APP_NAME} home`}
          >
            <img
              src="/icon.png"
              alt=""
              className="h-8 w-8 rounded-lg"
              width={32}
              height={32}
            />
            <span
              className={cn(
                "text-base font-semibold tracking-tight",
                onDark ? "text-white" : "text-foreground",
              )}
            >
              {APP_NAME}
            </span>
          </button>

          {/* Desktop nav */}
          <nav
            aria-label="Primary"
            className="hidden grow items-center justify-center gap-1 md:flex"
          >
            {NAV_LINKS.map((link) => (
              <Button
                key={link.name}
                variant="ghost"
                size="sm"
                onClick={() => handleNav(link.href)}
                className={
                  onDark
                    ? "text-white/60 hover:bg-white/10 hover:text-white"
                    : "text-muted-foreground hover:text-foreground"
                }
              >
                {link.name}
              </Button>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <div className="hidden md:block">
              <ThemeToggle
                theme={theme}
                resolvedTheme={resolvedTheme}
                setTheme={setTheme}
                className={themeToggleClass}
              />
            </div>
            <Button
              onClick={handleSignIn}
              size="sm"
              className="hidden h-9 gap-1.5 sm:inline-flex"
            >
              Sign in
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>

            {/* Mobile menu */}
            <div className="flex items-center gap-1 md:hidden">
              <div className="md:hidden">
                <ThemeToggle
                  theme={theme}
                  resolvedTheme={resolvedTheme}
                  setTheme={setTheme}
                  variant="icon"
                  className={themeToggleClass}
                />
              </div>
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    aria-label="Open menu"
                  >
                    <Menu className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  className="w-[85%] max-w-sm border-l-border/60 bg-background p-0"
                >
                  <SheetHeader className="border-b border-border/60 px-5 py-4">
                    <SheetTitle className="flex items-center gap-2 text-left">
                      <img
                        src="/icon.png"
                        alt=""
                        className="h-8 w-8 rounded-lg"
                        width={32}
                        height={32}
                      />
                      <span className="font-semibold">{APP_NAME}</span>
                    </SheetTitle>
                  </SheetHeader>
                  <nav
                    aria-label="Mobile"
                    className="flex flex-col gap-1 px-3 py-4"
                  >
                    {NAV_LINKS.map((link) => (
                      <Button
                        key={link.name}
                        variant="ghost"
                        className="h-11 justify-start text-base"
                        onClick={() => handleNav(link.href)}
                      >
                        {link.name}
                      </Button>
                    ))}
                    <div className="my-3 border-t border-border/60" />
                    <Button className="h-11 gap-2" onClick={handleSignIn}>
                      Sign in
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </nav>
                  <button
                    type="button"
                    aria-label="Close menu"
                    className={cn(
                      "absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-md focus-ring",
                      onDark
                        ? "text-white/60 hover:text-white"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    data-radix-collection-item=""
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default LandingHeader;
